import {
    useState,
    useEffect,
    useCallback,
    useRef,
    type Dispatch,
    type RefObject,
    type SetStateAction,
} from 'react';
import { useAppStore } from '@/store/useAppStore';
import { 
    subscribeToLatestScriptJob, 
    repairScriptImport,
    ScriptJobStatus,
    ScriptJobStatusValue
} from '@/features/sync/ai-api';

export type StatusIndicator = 'processing' | 'warning' | 'success' | null;

const DISMISSED_SCRIPT_JOBS_STORAGE_KEY = 'onbook-dismissed-script-jobs-v1';
const MAX_PERSISTED_DISMISSED_JOBS = 100;
const TERMINAL_STATUSES = new Set<ScriptJobStatusValue>(['completed', 'partial', 'failed']);
type ScriptJobWithId = ScriptJobStatus & { jobId: string };

export interface ScriptJobState {
    /** Current job status value */
    status: ScriptJobStatusValue | null;
    /** Firestore job ID */
    jobId: string | null;
    /** Total blocks extracted */
    blockCount: number;
    /** Number of failed block batches */
    failedBatchCount: number;
    /** Phases that failed (computed from phaseErrors + failedBatches) */
    failedPhases: string[];
    /** Error message if failed */
    error: string | null;
    /** Whether a repair is currently in progress */
    isRepairing: boolean;
    /** Repair result from the last attempt */
    repairResult: Record<string, unknown> | null;
    /** Trigger a repair for the given phases */
    repairJob: (phases: ('blocks' | 'structure' | 'props' | 'soundCues')[]) => Promise<void>;
    /** Dismiss the status indicator (hides this terminal job across reloads) */
    dismiss: () => void;
    /** Whether the user has dismissed the current status */
    isDismissed: boolean;
    /** Sidebar-compatible status indicator */
    indicator: StatusIndicator;
}

function dismissedJobStorageAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readDismissedJobMap(): Record<string, number> {
    if (!dismissedJobStorageAvailable()) {
        return {};
    }

    try {
        const raw = window.localStorage.getItem(DISMISSED_SCRIPT_JOBS_STORAGE_KEY);
        if (!raw) return {};

        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }

        return Object.fromEntries(
            Object.entries(parsed)
                .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
        );
    } catch {
        return {};
    }
}

function writeDismissedJobMap(dismissedJobs: Record<string, number>): void {
    if (!dismissedJobStorageAvailable()) {
        return;
    }

    try {
        window.localStorage.setItem(
            DISMISSED_SCRIPT_JOBS_STORAGE_KEY,
            JSON.stringify(dismissedJobs)
        );
    } catch {
        // Storage can be unavailable in private browsing or quota pressure.
        // The in-memory dismissed state still prevents this session from re-showing.
    }
}

function buildDismissedJobKey(orgId: string, projectId: string, userId: string, jobId: string): string {
    return `${orgId}/${projectId}/${userId}/${jobId}`;
}

function hasPersistedDismissal(orgId: string, projectId: string, userId: string, jobId: string): boolean {
    const key = buildDismissedJobKey(orgId, projectId, userId, jobId);
    return Object.hasOwn(readDismissedJobMap(), key);
}

function persistDismissedJob(orgId: string, projectId: string, userId: string, jobId: string): void {
    const key = buildDismissedJobKey(orgId, projectId, userId, jobId);
    const dismissedJobs = {
        ...readDismissedJobMap(),
        [key]: Date.now(),
    };

    const prunedDismissals = Object.fromEntries(
        Object.entries(dismissedJobs)
            .toSorted((a, b) => b[1] - a[1])
            .slice(0, MAX_PERSISTED_DISMISSED_JOBS)
    );

    writeDismissedJobMap(prunedDismissals);
}

function deriveFailedPhases(job: ScriptJobWithId | null): string[] {
    if (!job) {
        return [];
    }

    const phaseErrors = job.phaseErrors ? Object.keys(job.phaseErrors) : [];
    return job.failedBatches?.length ? [...phaseErrors, 'blocks'] : phaseErrors;
}

function deriveStatusIndicator(
    job: ScriptJobWithId | null,
    isDismissed: boolean,
): StatusIndicator {
    if (!job || isDismissed) {
        return null;
    }

    if (['processing', 'stage_1', 'stage_2', 'repairing'].includes(job.status)) {
        return 'processing';
    }

    if (['partial', 'failed'].includes(job.status)) {
        return 'warning';
    }

    return job.status === 'completed' ? 'success' : null;
}

type ScriptJobUpdateContext = {
    jobData: ScriptJobWithId | null;
    lastJobIdRef: RefObject<string | null>;
    orgId: string;
    projectId: string;
    userId: string;
    setJob: Dispatch<SetStateAction<ScriptJobWithId | null>>;
    setIsDismissed: Dispatch<SetStateAction<boolean>>;
    setIsRepairing: Dispatch<SetStateAction<boolean>>;
    setRepairResult: Dispatch<SetStateAction<Record<string, unknown> | null>>;
};

function applyScriptJobUpdate({
    jobData,
    lastJobIdRef,
    orgId,
    projectId,
    userId,
    setJob,
    setIsDismissed,
    setIsRepairing,
    setRepairResult,
}: ScriptJobUpdateContext): void {
    if (!jobData) {
        setJob(null);
        setIsRepairing(false);
        return;
    }

    if (jobData.jobId !== lastJobIdRef.current) {
        lastJobIdRef.current = jobData.jobId;
        setIsDismissed(hasPersistedDismissal(orgId, projectId, userId, jobData.jobId));
        setRepairResult(null);
    }
    setJob(jobData);

    if (jobData.status !== 'repairing') {
        setIsRepairing(false);
    }

    if (TERMINAL_STATUSES.has(jobData.status)) {
        useAppStore.getState().showStructureActions.setActiveJobId(null);
    }
}

/**
 * Hook that subscribes to the latest script job for the current project.
 * Returns job status, repair actions, and a sidebar-compatible status indicator.
 */
export function useScriptJobStatus(): ScriptJobState {
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const userId = useAppStore(state => state.auth.user?.uid ?? null);
    const [job, setJob] = useState<ScriptJobWithId | null>(null);
    const [isRepairing, setIsRepairing] = useState(false);
    const [repairResult, setRepairResult] = useState<Record<string, unknown> | null>(null);
    const [isDismissed, setIsDismissed] = useState(false);
    const lastJobIdRef = useRef<string | null>(null);

    // Subscribe to latest job — depends on both projectId AND userId
    useEffect(() => {
        if (!orgId || !projectId || !userId) {
            setJob(null);
            return;
        }

        const scope = { orgId, projectId };
        const unsubscribe = subscribeToLatestScriptJob(scope, (jobData) => applyScriptJobUpdate({
            jobData,
            lastJobIdRef,
            orgId,
            projectId,
            userId,
            setJob,
            setIsDismissed,
            setIsRepairing,
            setRepairResult,
        }), userId);

        return unsubscribe;
    }, [orgId, projectId, userId]);

    const failedPhases = deriveFailedPhases(job);
    const indicator = deriveStatusIndicator(job, isDismissed);

    const repairJob = useCallback(async (phases: ('blocks' | 'structure' | 'props' | 'soundCues')[]) => {
        if (!orgId || !projectId || !job?.jobId) return;
        
        setIsRepairing(true);
        setRepairResult(null);
        try {
            const result = await repairScriptImport({ orgId, projectId }, job.jobId, phases);
            setRepairResult(result.results);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Repair failed';
            setRepairResult({ error: msg });
        } finally {
            setIsRepairing(false);
        }
    }, [orgId, projectId, job?.jobId]);

    const dismiss = useCallback(() => {
        if (orgId && projectId && userId && job?.jobId && TERMINAL_STATUSES.has(job.status)) {
            persistDismissedJob(orgId, projectId, userId, job.jobId);
        }
        setIsDismissed(true);
    }, [job?.jobId, job?.status, orgId, projectId, userId]);

    return {
        status: job?.status ?? null,
        jobId: job?.jobId ?? null,
        blockCount: job?.blockCount ?? 0,
        failedBatchCount: job?.failedBatches?.length ?? 0,
        failedPhases,
        error: job?.error ?? null,
        isRepairing,
        repairResult,
        repairJob,
        dismiss,
        isDismissed,
        indicator,
    };
}
